import React, { useState } from 'react';
import styled from 'styled-components';
import { VerifierSelectInputRecord } from 'types/dynamic-forms/records/VerifierSelectInputRecord';
import usePropertyPath from '../../hooks/usePropertyPath';
import useProperty from '../../hooks/useProperty';
import WizardSelectControlledComponent from 'components/v2/epd-wizard-inputs/WizardSelectControlled';
import useDataSource from '../../hooks/useDataSource';
import { VerifierType } from 'types/types';
import WizardTextInputControlledComponent from 'components/v2/epd-wizard-inputs/WizardTextInputControlled';
import WizardAddButton from 'components/v2/epd-wizard-inputs/WizardAddButton';
import { useDocumentStore } from '../../hooks/useDocumentStore';
import VerifierSelectInputRendererDialog from './VerifierSelectInputRendererDialog';
import { AddVerifierToCompanyModel } from 'types/dynamic-forms/companies/AddVerifierToCompanyModel';
import { postAddVerifierToCompany } from 'services/api/dynamicFormsCompaniesApi';
import { formatCompanyAddress } from 'util/utils';
import { InfoErrorContainer } from 'components/v2/help-boxes/InfoErrorContainer';
import { VERIFIER_NOT_AVAILABLE_MESSAGE } from '../../store/validation-resolvers/verifierAvailableValidationRuleResolver';

const VerifierSelectInputRenderer: React.FC<VerifierSelectInputRecord> = ({ propertyId, label, tooltip, readOnly }) => {
  const store = useDocumentStore();
  const verifierUserPropertyPath = usePropertyPath(propertyId);
  const { value, setValue, errorMessage, isDisabled, isVisible } = useProperty<string | null>(verifierUserPropertyPath);
  const { options, invalidateDataSource } = useDataSource(verifierUserPropertyPath);
  const [showSelectDialog, setShowSelectDialog] = useState<boolean>(false);

  if (!isVisible) {
    return null;
  }

  const handleAddVerifier = async (model: AddVerifierToCompanyModel) => {
    const addResult = await postAddVerifierToCompany(store.metadata.companyId, model);
    await invalidateDataSource();
    setValue(addResult.userId);
  };

  // The current value is looked up in the full list so a discontinued (disabled) verifier is still
  // shown as the value, while only selectable options are offered in the dropdown.
  const currentOption = options.find((o) => o.value === value);
  const selectableOptions = options.filter((o) => !o.isDisabled);
  // Every error keeps the label error icon. The "verifier not available" error is also shown as a help
  // box below the add button (design node 3275:1384, which draws both the icon and the box).
  const discontinuedVerifierError = errorMessage === VERIFIER_NOT_AVAILABLE_MESSAGE ? errorMessage : undefined;
  const verifierLabel = label ?? 'EPD Verifier';
  const verifierTooltip =
    tooltip ??
    'The third-party verifier responsible for verifying this EPD. The EPD Owner can manage available verifiers and add new verifiers.';
  const verifierDisplayValue = currentOption
    ? currentOption.label
    : value ?? 'Please select the EPD Verifier in Step 1 - General information';

  return (
    <>
      {!readOnly && (
        <div>
          <WizardSelectControlledComponent
            name={propertyId}
            options={selectableOptions}
            // null, not undefined: react-select treats undefined as uncontrolled and keeps showing the
            // option it held at mount, e.g. a verifier the server removed before the document reload.
            value={currentOption ?? null}
            label={verifierLabel}
            tooltip={verifierTooltip}
            onChanged={(_: string, value: any) => setValue(value ?? null)}
            error={errorMessage}
            disabled={isDisabled}
          />

          {!isDisabled && <WizardAddButton label="+ Add EPD Verifier" onClick={() => setShowSelectDialog(true)} />}

          {discontinuedVerifierError && (
            <DiscontinuedVerifierErrorBox>
              <InfoErrorContainer infoText={discontinuedVerifierError} />
            </DiscontinuedVerifierErrorBox>
          )}
        </div>
      )}

      {!readOnly && (
        <VerifierSelectInputRendererDialog
          isOpened={showSelectDialog}
          canPrefetch={!isDisabled}
          onHide={() => setShowSelectDialog(false)}
          companyId={store.metadata.companyId}
          onAddVerifier={handleAddVerifier}
        />
      )}

      {/* One block, like the editable branch: as separate field-panel grid items the input and the help
          box would be 30px apart instead of 16px. */}
      {readOnly && (
        <div>
          <WizardTextInputControlledComponent
            label={verifierLabel}
            tooltip={verifierTooltip}
            name={`${propertyId}-readonly`}
            value={verifierDisplayValue}
            error={errorMessage}
            disabled
          />

          {discontinuedVerifierError && (
            <DiscontinuedVerifierErrorBox>
              <InfoErrorContainer infoText={discontinuedVerifierError} />
            </DiscontinuedVerifierErrorBox>
          )}
        </div>
      )}

      {!readOnly && currentOption?.meta && (
        <>
          {currentOption?.meta.type === VerifierType.Body && (
            <>
              <WizardTextInputControlledComponent
                label="Accredited certification body address"
                name="acbAddress"
                value={formatCompanyAddress(
                  {
                    country: currentOption.meta.country,
                    city: currentOption.meta.city ?? null,
                    zipCode: currentOption.meta.zipCode ?? null,
                    addressLine: currentOption.meta.addressLine ?? null,
                  },
                  currentOption.meta.country
                )}
                disabled
              />
              <WizardTextInputControlledComponent
                label="Certification body accredited by"
                name="acbAccreditedBy"
                value={currentOption.meta.approvedBy}
                disabled
              />
            </>
          )}
          {currentOption?.meta.type === VerifierType.Individual && (
            <WizardTextInputControlledComponent
              label="Individual verifier approved by"
              name="verifierApprovedBy"
              value={currentOption.meta.approvedBy}
              disabled
            />
          )}
        </>
      )}
    </>
  );
};

// Design node 3275:1384: the help box sits 16px below the control above it (the add button, or the
// select / read-only input when the button is not rendered) and 30px above the next field. The field
// panel (FieldPanelFullWidth) is a grid with a 30px row gap, and a grid item contains its children's
// margins, so InfoErrorContainer's own margin-bottom of 16px would widen that gap to 46px. It is reset
// here, because the shared InfoErrorContainer is used by other screens with its margin.
const DiscontinuedVerifierErrorBox = styled.div`
  margin-top: 16px;

  & > div {
    margin-bottom: 0;
  }
`;

export default VerifierSelectInputRenderer;
